import type { Category, Consent, Decision, Choice } from './types';

export const COOKIE_NAME = 'cookiesie_consent';
// 180 days — DPC-mandated six-month renewal cap. mvp_b_plan.md §3.4 / §5.2.
export const COOKIE_MAX_AGE_S = 180 * 24 * 60 * 60;
export const PENDING_KEY = 'cookiesie_pending';

export const ALL_CATEGORIES: Category[] = [
  'strictly_necessary',
  'functional',
  'analytics',
  'marketing',
];

export function emptyConsent(): Consent {
  return {
    strictly_necessary: true,
    functional: false,
    analytics: false,
    marketing: false,
  };
}

export function fullConsent(): Consent {
  return {
    strictly_necessary: true,
    functional: true,
    analytics: true,
    marketing: true,
  };
}

export function readDecision(): Decision | null {
  const fromCookie = readCookie();
  if (fromCookie) return fromCookie;
  try {
    const raw = window.localStorage.getItem(COOKIE_NAME);
    if (!raw) return null;
    return parseDecision(raw);
  } catch {
    return null;
  }
}

export function writeDecision(decision: Decision): void {
  const value = JSON.stringify(decision);
  writeCookie(value);
  try {
    window.localStorage.setItem(COOKIE_NAME, value);
  } catch {
    // private mode etc. — cookie still serves as source of truth.
  }
}

export function clearDecision(): void {
  // Expire the cookie immediately.
  document.cookie = `${COOKIE_NAME}=; Max-Age=0; Path=/; SameSite=Lax${cookieSecure()}`;
  try {
    window.localStorage.removeItem(COOKIE_NAME);
  } catch {
    // ignore
  }
}

function readCookie(): Decision | null {
  const prefix = `${COOKIE_NAME}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      try {
        return parseDecision(decodeURIComponent(trimmed.slice(prefix.length)));
      } catch {
        return null;
      }
    }
  }
  return null;
}

function writeCookie(value: string): void {
  document.cookie =
    `${COOKIE_NAME}=${encodeURIComponent(value)}` +
    `; Max-Age=${COOKIE_MAX_AGE_S}; Path=/; SameSite=Lax${cookieSecure()}`;
}

function cookieSecure(): string {
  return location.protocol === 'https:' ? '; Secure' : '';
}

function parseDecision(raw: string): Decision | null {
  const obj = JSON.parse(raw) as Decision;
  if (!obj || obj.v !== 1 || typeof obj.pr !== 'number' || !obj.c) return null;
  // Coerce missing categories to false; force strictly_necessary true.
  const c: Consent = emptyConsent();
  for (const k of ALL_CATEGORIES) c[k] = !!obj.c[k];
  c.strictly_necessary = true;
  return { v: 1, c, pr: obj.pr, ts: obj.ts ?? Date.now(), choice: obj.choice ?? 'custom' };
}

// Local consent string (mvp_b_plan.md §8.5): `v1:` + 4-bit hex bitfield in
// fixed order [necessary, functional, analytics, marketing].
export function consentString(c: Consent): string {
  let bits = 0;
  if (c.strictly_necessary) bits |= 1;
  if (c.functional) bits |= 2;
  if (c.analytics) bits |= 4;
  if (c.marketing) bits |= 8;
  return `v1:${bits.toString(16)}`;
}

export function buildDecision(c: Consent, pr: number, choice: Choice): Decision {
  return { v: 1, c, pr, ts: Date.now(), choice };
}
